import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle } from "react-feather";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Tab,
} from "react-bootstrap";
import { toast } from "react-toastify";
// ✅ Real API — no mockApi
import { getJobDetailsAPI, retryJobAPI } from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./FailureDetail.module.scss";

// ── Status → Badge color map ──────────────────────────────
const STATUS_VARIANT = {
  queued: "secondary",
  running: "primary",
  success: "success",
  failed: "danger",
};

export default function FailureDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [payloadWrap, setPayloadWrap] = useState(true);
  const [stackWrap, setStackWrap] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getJobDetailsAPI(id);
      // Backend: GET /jobs/:jobId  →  { success, data: Job }
      setData(res.data?.data ?? res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const copyText = (label, text) => {
    navigator.clipboard?.writeText(text || "");
    toast.success(`${label} copied`);
  };

  // ── Loading state ─────────────────────────────────────
  if (loading && !data) {
    return (
      <div>
        <PageHeader
          title="Job detail"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Jobs", path: platformUrl("jobs") },
            { label: id },
          ]}
        />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────
  if (error && !data) {
    return (
      <div>
        <PageHeader
          title="Job detail"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Jobs", path: platformUrl("jobs") },
            { label: id },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load job"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  // ── Map flat backend Job fields ───────────────────────
  // Backend Job model: { id, queue, tenantCode, tenantName, type,
  //   status, createdAt, startedAt, completedAt, durationMs, error, retryCount }
  // NOTE: backend does NOT return a nested "summary", "suggestedFix",
  // "payloadJson", "stackTrace", "retryHistory", or "companyName".
  // These fields are rendered gracefully with fallbacks below.
  const job = data;

  return (
    <div>
      <PageHeader
        title={`${job.type}`}
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Jobs", path: platformUrl("jobs") },
          { label: id },
        ]}
      />

      {/* ── Summary card ── */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 small">
            <Col md={4}>
              <span className="text-muted">Job ID</span>
              <div className="fw-semibold">{job?.id}</div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Queue</span>
              <div className="fw-semibold">{job?.queue}</div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Tenant</span>
              <div className="fw-semibold">
                {job?.tenantCode}
                {job?.tenantName ? ` — ${job.tenantName}` : ""}
              </div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Type</span>
              <div className="fw-semibold">{job?.type}</div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Created</span>
              <div>
                {job?.createdAt
                  ? new Date(job.createdAt).toLocaleString()
                  : "—"}
              </div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Started</span>
              <div>
                {job?.startedAt
                  ? new Date(job.startedAt).toLocaleString()
                  : "—"}
              </div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Completed</span>
              <div>
                {job?.completedAt
                  ? new Date(job.completedAt).toLocaleString()
                  : "—"}
              </div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Duration</span>
              <div>
                {job?.durationMs != null ? `${job.durationMs} ms` : "—"}
              </div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Attempts</span>
              <div>{job?.retryCount ?? "—"}</div>
            </Col>
            <Col md={4}>
              <span className="text-muted">Status</span>
              <div>
                <Badge bg={STATUS_VARIANT[job?.status] ?? "secondary"}>
                  {job?.status}
                </Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Error banner — shown only when job has failed ── */}
      {job?.error && (
        <div className={`${styles.errorBox} mb-4`}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <div className="text-danger fw-bold fs-5">Error</div>
              <div className="text-danger">{job.error}</div>
            </div>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => copyText("Error", job.error)}
            >
              Copy error
            </Button>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <Tab.Container defaultActiveKey="payload">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="payload">Payload</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="stack">Stack trace</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="retry">Retry history</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          {/* Payload — backend stores raw payload in job object; fallback to JSON */}
          <Tab.Pane eventKey="payload">
            <div className="d-flex gap-2 mb-2">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setPayloadWrap((v) => !v)}
              >
                {payloadWrap ? "No wrap" : "Word wrap"}
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() =>
                  copyText(
                    "Payload",
                    job?.payloadJson ?? JSON.stringify(job, null, 2),
                  )
                }
              >
                Copy payload
              </Button>
            </div>
            <pre
              className={`${styles.codeBlock} ${!payloadWrap ? styles.codeBlockNowrap : ""}`}
            >
              {job?.payloadJson ?? JSON.stringify(job, null, 2)}
            </pre>
          </Tab.Pane>

          {/* Stack trace — not in current backend model; shows placeholder */}
          <Tab.Pane eventKey="stack">
            <Button
              size="sm"
              variant="outline-secondary"
              className="mb-2"
              onClick={() => setStackWrap((v) => !v)}
            >
              {stackWrap ? "No-wrap" : "Wrap"}
            </Button>
            <pre
              className={`${styles.codeBlock} ${!stackWrap ? styles.codeBlockNowrap : ""}`}
            >
              {job?.stackTrace ?? "No stack trace available for this job."}
            </pre>
          </Tab.Pane>

          {/* Retry history — not in current backend model */}
          <Tab.Pane eventKey="retry">
            {(job?.retryHistory ?? []).length === 0 ? (
              <p className="text-muted small mt-2">
                No retry history available.
              </p>
            ) : (
              <Accordion>
                {job.retryHistory.map((h, i) => (
                  <Accordion.Item eventKey={String(i)} key={h.attempt ?? i}>
                    <Accordion.Header>
                      Attempt {h.attempt} —{" "}
                      {h.ts ? new Date(h.ts).toLocaleString() : "—"} — {h.error}{" "}
                      ({h.durationMs} ms)
                    </Accordion.Header>
                    <Accordion.Body>
                      <pre className={styles.codeBlock}>{h.detail}</pre>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* ── Actions ── */}
      <div className="d-flex flex-wrap gap-2 mt-4 pt-3 border-top">
        <Button
          disabled={job?.status !== "failed"}
          onClick={() => setRetryOpen(true)}
        >
          Retry now
        </Button>
      </div>

      {/* ── Retry confirm ── */}
      <ConfirmModal
        isOpen={retryOpen}
        onClose={() => setRetryOpen(false)}
        onConfirm={async () => {
          try {
            await retryJobAPI(id);
            toast.success("Job re-queued");
            setRetryOpen(false);
            load();
          } catch (e) {
            toast.error(e.response?.data?.error || e.message);
          }
        }}
        title="Retry job?"
        message={`This will immediately re-queue job ${id}.`}
        confirmLabel="Retry"
      />
    </div>
  );
}
